import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useContext, useEffect, useState, type JSX } from 'react';

import { IntegrationSystem, SelectOption } from '@/shared/types';
import { Image } from '@/shared/ui';

import { FormRow, FormInputWrapper } from '@/components/old';

import { getOrgNpuSelect } from '@/api/apiService';
import { DocumentTypeCode, DocumentFieldName, FormDocument, DocumentTypeEntity } from '@/api/types';

import ModalContext from '../../../contexts/Modal/ModalContext';
import { ModalName } from '../../../contexts/Modal/modalTypes';
import { replaceUnderscores } from '../../../helpers/replaceUnderscores';
import { Button } from '../../Button';
import InputDate from '../../Inputs/InputDate';
import { InputDateAnt } from '../../Inputs/InputDateAnt';
import { FormConstants } from '../constants';
import FileUpload from '../FileUpload';
import { InputField } from '../Inputs/InputField';
import InitialSelect from '../Select';
import {
  isLatinLetterRegExp,
  isRussianLetterRegExp,
  isUkrainianLetterRegExp,
  ValidateInputValueResult,
} from '../Validation';

import { MAX_DOCUMENTS_DEFAULT } from './utils';

interface Props<System extends IntegrationSystem> {
  documentTypeLabel: string;
  documentUploadLabel?: string;
  documentTypeEntities: Array<DocumentTypeEntity<System>>;
  maxDocumentsCount?: number;
  documents: Array<FormDocument<System>>;
  name: string;
  onDocumentTypeSelectOpen?: () => void;
  handleChange: (documents: Array<FormDocument<System>>) => void;
  handleInputChange?: (name: string, value: unknown) => void;
  handleClearInput?: (name: string) => void;
  withFileUpload?: boolean;
  isAnt?: boolean;
  documentsRequired?: boolean;
  documentTypeSelectDisabled?: boolean;
  skipDuplicateValidation?: boolean;
}

const quantityFilesByDocumentType: Partial<Record<DocumentTypeCode, number>> = {
  [DocumentTypeCode.PHOTO_CARD]: 1,
  [DocumentTypeCode.PAYMENT_REFERENCE]: 1,
  [DocumentTypeCode.PASSPORT]: 6,
};

export const DocumentsData = <System extends IntegrationSystem>({
  documentTypeLabel,
  documentUploadLabel,
  documentTypeEntities: documentTypeOptions,
  onDocumentTypeSelectOpen,
  maxDocumentsCount = MAX_DOCUMENTS_DEFAULT,
  documents = [],
  name,
  handleChange,
  handleInputChange,
  handleClearInput,
  withFileUpload = false,
  isAnt = false,
  documentsRequired = false,
  documentTypeSelectDisabled = false,
  skipDuplicateValidation = false,
}: Props<System>): JSX.Element => {
  const { renderSpecificModal } = useContext(ModalContext);

  const [optionsOrgNpu, setOptionsOrgNpu] = useState<SelectOption[]>([]);

  const addEmptyDocument = useCallback((): void => {
    handleChange([
      ...documents,
      {
        type: undefined,
        info: {},
        attachments: [],
      },
    ]);
  }, [documents]);

  const checkForDuplicates = useCallback(
    (docType: FormDocument<System>['type']): boolean => {
      const hasDuplicate = documents.some((document) => document.type?.code === docType?.code);

      hasDuplicate &&
        renderSpecificModal({
          modalName: ModalName.DocumentAddCheckAlert,
        });

      return hasDuplicate;
    },
    [documents, renderSpecificModal],
  );

  useEffect(() => {
    if (!documents?.length) {
      addEmptyDocument();
    } else if (handleInputChange) {
      documents.forEach((document, index) => {
        Object.entries(document).forEach(([documentFieldName, documentFieldValue]) => {
          if (documentFieldName === 'info' && !isEmpty(documentFieldValue)) {
            Object.entries(documentFieldValue).forEach(([infoFieldName, infoFieldValue]) => {
              // WARN: do not use here lodash.isEmpty because:
              // According to the lodash.isEmpty documentation "Objects are considered empty if they have no own enumerable string keyed properties".
              // Considering the Object.getOwnPropertyNames methods, all Date objects are empty
              !!infoFieldValue && handleInputChange(`${name}_${index}_${infoFieldName}`, 'testValueForValidation');
            });
          }

          if (
            documentFieldName === DocumentFieldName.type &&
            isEmpty(documentFieldValue) &&
            documentTypeOptions.length === 1
          ) {
            onDocumentChange(index, {
              ...documents[index],
              type: documentTypeOptions[0],
              info: {},
            });
          }

          !isEmpty(documentFieldValue) &&
            handleInputChange(`${name}_${index}_${documentFieldName}`, 'testValueForValidation');
        });
      });
    }
  }, [documents, documentTypeOptions]);

  useEffect(() => {
    if (!optionsOrgNpu.length && documents.some((document) => document.type?.code === DocumentTypeCode.PERMIT))
      getOrgNpuSelect(setOptionsOrgNpu);
  }, [documents]);

  const dynamicDocumentInfoFields = (index: number, onChange: (name: string, value: any) => void): JSX.Element => {
    const documentFormStateName = (i: string): string => `${name}_${index}_` + i;
    const document = documents[index];

    switch (document.type?.code) {
      case DocumentTypeCode.APPLICATION:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата заяви"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата заяви"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );
      case DocumentTypeCode.LEASE_AGREEMENT:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер договору"
                value={document.info?.serialNumberDoc}
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />
              <InputField
                name={documentFormStateName(DocumentFieldName.indexNumber)}
                label="Індексний номер"
                value={document.info?.indexNumber}
                change={(_, value): void => onChange(DocumentFieldName.indexNumber, value)}
              />
            </FormRow>

            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  minDateBirth
                  maxDateToday
                  label="Дата підписання"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  minDateBirth
                  maxDateToday
                  label="Дата підписання"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}

              {isAnt ? (
                <InputDateAnt
                  minDateToday
                  label="Строк оренди"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? dayjs(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date?.toDate())}
                />
              ) : (
                <InputDate
                  minDateToday
                  label="Строк оренди"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.OWNERSHIP_CERTIFICATE:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Серія та номер"
                value={document.info?.serialNumberDoc}
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />

              <InputField
                name={documentFormStateName(DocumentFieldName.indexNumber)}
                label="Індексний номер"
                value={document.info?.indexNumber}
                change={(_, value): void => onChange(DocumentFieldName.indexNumber, value)}
              />
            </FormRow>

            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value)}
              />
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.EXRACT_PRPR:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер запису про право власності"
                value={document.info?.serialNumberDoc}
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />

              <InputField
                name={documentFormStateName(DocumentFieldName.indexNumber)}
                label="Індексний номер"
                value={document.info?.indexNumber}
                change={(_, value): void => onChange(DocumentFieldName.indexNumber, value)}
              />
            </FormRow>

            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}

              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Ким сформовано"
                value={document.info?.auth_dep}
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value)}
              />
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.CERT_MARRIAGE:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Серія та номер"
                value={document.info?.serialNumberDoc}
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />

              {isAnt ? (
                <InputDateAnt
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>

            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value)}
              />
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />

              {isAnt ? (
                <InputDateAnt
                  required
                  maxDateToday
                  label="Дата формування"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  maxDateToday
                  label="Дата формування"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>

            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                required
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value)}
              />
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.LEGAL_RESIDENCE:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />

              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                required
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value)}
              />
            </FormRow>

            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата формування"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              ) : (
                <InputDate
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата формування"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}

              {isAnt ? (
                <InputDateAnt
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? dayjs(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              ) : (
                <InputDate
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.CERT_IDP:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />

              {isAnt ? (
                <InputDateAnt
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.PERMIT:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />

              {isAnt ? (
                <InputDateAnt
                  required
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>

            <FormRow className="form__create-js" data-doc={document.type.code}>
              <FormInputWrapper label="Орган видачі" required className="no-group">
                <div className="input-select js-input-select">
                  <InitialSelect
                    name={documentFormStateName(DocumentFieldName.auth_dep)}
                    options={optionsOrgNpu}
                    placeholder="Не вибрано"
                    defaultValue={optionsOrgNpu.find((orgNpu) => orgNpu.id === document?.info?.auth_dep)}
                    onChange={(newValue): void => onChange(DocumentFieldName.auth_dep, newValue.id)}
                    onMenuOpen={(): void => {
                      if (!optionsOrgNpu.length) {
                        getOrgNpuSelect(setOptionsOrgNpu);
                      }
                    }}
                  />
                </div>
              </FormInputWrapper>
              {isAnt ? (
                <InputDateAnt
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? dayjs(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date?.toDate())}
                />
              ) : (
                <InputDate
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.POWER_OF_ATTORNEY:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />

              {isAnt ? (
                <InputDateAnt
                  required
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>

            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  required
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? dayjs(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.INSURANCE_POLICY:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Серія та номер"
                value={document.info?.serialNumberDoc}
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}

              {isAnt ? (
                <InputDateAnt
                  required
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? dayjs(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.EXPERT_RESEARCH:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                required
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value)}
              />

              {isAnt ? (
                <InputDateAnt
                  required
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.PASSPORT:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                withTextMask
                inputMaskType="text"
                mask={[isUkrainianLetterRegExp, isUkrainianLetterRegExp, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Серія та номер"
                value={document.info?.serialNumberDoc}
                required
                minLength={8}
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value?.toUpperCase())}
                formatInputValue={replaceUnderscores}
              />
              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                required
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value.toUpperCase())}
                validator={(value): ValidateInputValueResult => {
                  if (isRussianLetterRegExp.test(value)) {
                    return {
                      isValid: false,
                      error: 'Не передбачено використання символів російського алфавіту',
                    };
                  } else if (isLatinLetterRegExp.test(value)) {
                    return {
                      isValid: false,
                      error: 'Не передбачено використання символів латинського алфавіту',
                    };
                  } else {
                    return {
                      isValid: true,
                      error: undefined,
                    };
                  }
                }}
                maxLength={FormConstants.INPUT_MAX_LENGTH}
              />
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.INTERNATIONAL_PASSPORT:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                withTextMask
                inputMaskType="text"
                mask={[isLatinLetterRegExp, isLatinLetterRegExp, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Серія та номер"
                value={document.info?.serialNumberDoc}
                required
                minLength={8}
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value?.toUpperCase())}
                formatInputValue={replaceUnderscores}
              />
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}

              {isAnt ? (
                <InputDateAnt
                  required
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? dayjs(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              )}
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                withTextMask
                inputMaskType="text"
                mask={[/\d/, /\d/, /\d/, /\d/]}
                minLength={4}
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                required
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value?.toUpperCase())}
                formatInputValue={replaceUnderscores}
              />
              <InputField
                withMask
                type="text"
                minLength={14}
                inputMaskType="text"
                mask="99999999-99999"
                name={documentFormStateName(DocumentFieldName.unzr)}
                label="УНЗР"
                value={document.info?.unzr}
                required
                change={(_, value): void => onChange(DocumentFieldName.unzr, value)}
                formatInputValue={replaceUnderscores}
              />
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.ID_CARD:
      case DocumentTypeCode.RESIDENCE:
      case DocumentTypeCode.TEMPORARY_RESIDENCE:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                withMask
                inputMaskType="text"
                mask="999999999"
                minLength={9}
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер"
                placeholder="Не вказано"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
                formatInputValue={replaceUnderscores}
              />
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}

              {isAnt ? (
                <InputDateAnt
                  required
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? dayjs(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateToday
                  label="Термін дії"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              )}
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                withTextMask
                inputMaskType="text"
                mask={[/\d/, /\d/, /\d/, /\d/]}
                minLength={4}
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                required
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value?.toUpperCase())}
                formatInputValue={replaceUnderscores}
              />
              <InputField
                withMask
                type="text"
                minLength={14}
                inputMaskType="text"
                mask="99999999-99999"
                name={documentFormStateName(DocumentFieldName.unzr)}
                label="УНЗР"
                value={document.info?.unzr}
                required
                change={(_, value): void => onChange(DocumentFieldName.unzr, value)}
                formatInputValue={replaceUnderscores}
              />
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.OLD_RESIDENCE:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                withTextMask
                inputMaskType="text"
                mask={[isUkrainianLetterRegExp, isUkrainianLetterRegExp, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                minLength={8}
                label="Серія та номер"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value?.toUpperCase())}
                formatInputValue={replaceUnderscores}
              />
              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                required
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value.toUpperCase())}
                validator={(value): ValidateInputValueResult => {
                  if (isRussianLetterRegExp.test(value)) {
                    return {
                      isValid: false,
                      error: 'Не передбачено використання символів російського алфавіту',
                    };
                  } else if (isLatinLetterRegExp.test(value)) {
                    return {
                      isValid: false,
                      error: 'Не передбачено використання символів латинського алфавіту',
                    };
                  } else {
                    return {
                      isValid: true,
                      error: undefined,
                    };
                  }
                }}
                maxLength={FormConstants.INPUT_MAX_LENGTH}
              />
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );
      case DocumentTypeCode.MEDICAL_REFERENCE:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                withTextMask
                inputMaskType="text"
                mask={[isUkrainianLetterRegExp, isUkrainianLetterRegExp, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                minLength={8}
                label="Серія та номер"
                value={document.info?.serialNumberDoc}
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value?.toUpperCase())}
                formatInputValue={replaceUnderscores}
              />
              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value.toUpperCase())}
                validator={(value): ValidateInputValueResult => {
                  if (isRussianLetterRegExp.test(value)) {
                    return {
                      isValid: false,
                      error: 'Не передбачено використання символів російського алфавіту',
                    };
                  } else if (isLatinLetterRegExp.test(value)) {
                    return {
                      isValid: false,
                      error: 'Не передбачено використання символів латинського алфавіту',
                    };
                  } else {
                    return {
                      isValid: true,
                      error: undefined,
                    };
                  }
                }}
                maxLength={FormConstants.INPUT_MAX_LENGTH}
              />
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              {isAnt ? (
                <InputDateAnt
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  minDateBirth
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}

              {isAnt ? (
                <InputDateAnt
                  required
                  minDateToday
                  label="Дійсний до"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? dayjs(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  minDateToday
                  label="Дійсний до"
                  name={documentFormStateName(DocumentFieldName.expirationDate)}
                  value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                  onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.TECHNICAL_CONDITION:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.auth_dep)}
                label="Орган видачі"
                value={document.info?.auth_dep}
                required
                change={(_, value): void => onChange(DocumentFieldName.auth_dep, value)}
              />
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Номер"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />
              {isAnt ? (
                <InputDateAnt
                  required
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? dayjs(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date?.toDate())}
                />
              ) : (
                <InputDate
                  required
                  maxDateToday
                  label="Дата видачі"
                  name={documentFormStateName(DocumentFieldName.date)}
                  value={document.info?.date ? new Date(document.info.date) : null}
                  onChange={(date): void => onChange(DocumentFieldName.date, date)}
                />
              )}
            </FormRow>
          </React.Fragment>
        );

      case DocumentTypeCode.CONTRACT_SECURITY_ACTIVITIES:
        return (
          <React.Fragment key={document.type.code}>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputField
                name={documentFormStateName(DocumentFieldName.serialNumberDoc)}
                label="Серія та номер"
                value={document.info?.serialNumberDoc}
                required
                change={(_, value): void => onChange(DocumentFieldName.serialNumberDoc, value)}
              />
            </FormRow>
            <FormRow className="form__create-js" data-doc={document.type.code}>
              <InputDate
                required
                maxDateToday
                label="Дата видачі"
                name={documentFormStateName(DocumentFieldName.date)}
                value={document.info?.date ? new Date(document.info.date) : null}
                onChange={(date): void => onChange(DocumentFieldName.date, date)}
              />
              <InputDate
                required
                minDateToday
                label="Термін дії"
                name={documentFormStateName(DocumentFieldName.expirationDate)}
                value={document.info?.expirationDate ? new Date(document.info.expirationDate) : null}
                onChange={(date): void => onChange(DocumentFieldName.expirationDate, date)}
              />
            </FormRow>
          </React.Fragment>
        );
      default:
        return <React.Fragment />;
    }
  };

  const onDocumentChange = (documentIndex: number, newDocument: FormDocument<System>): void => {
    const newDocuments = [...documents];
    newDocuments[documentIndex] = newDocument;

    handleChange(newDocuments);
  };

  useEffect(() => {
    handleClearInput &&
      documents.forEach((document, index) => {
        if (!document[DocumentFieldName.type]) {
          handleClearInput(`${name}_${index}_${DocumentFieldName.type}`);
        }
        if (!document[DocumentFieldName.attachments]?.length) {
          handleClearInput(`${name}_${index}_${DocumentFieldName.attachments}`);
        }
      });
  }, [documents]);

  return (
    <>
      {documents.map((document, index) => {
        const typeFieldName = `${name}_${index}_${DocumentFieldName.type}`;
        const attachmentsFieldName = `${name}_${index}_${DocumentFieldName.attachments}`;

        return (
          <React.Fragment key={typeFieldName + attachmentsFieldName}>
            <FormInputWrapper
              label={documentTypeLabel}
              required={documentsRequired}
              marginBottomNone
              className="no-group"
            >
              <div className="input-select js-input-select">
                <InitialSelect
                  name={typeFieldName}
                  options={documentTypeOptions}
                  placeholder="Не вибрано"
                  defaultValue={document.type}
                  disabled={documentTypeSelectDisabled}
                  onChange={(newValue): void => {
                    const hasDuplicate =
                      !skipDuplicateValidation && maxDocumentsCount > 1 && checkForDuplicates(newValue);

                    if (!hasDuplicate) {
                      onDocumentChange(index, {
                        ...documents[index],
                        type: newValue,
                        info: {},
                      });
                      handleInputChange && handleInputChange(typeFieldName, newValue.id);
                      handleClearInput &&
                        [
                          DocumentFieldName.auth_dep,
                          DocumentFieldName.date,
                          DocumentFieldName.expirationDate,
                          DocumentFieldName.indexNumber,
                          DocumentFieldName.serialNumberDoc,
                          DocumentFieldName.unzr,
                        ].forEach((documentKeyName) => {
                          handleClearInput(`${name}_${index}_${documentKeyName}`);
                        });
                    }
                  }}
                  onMenuOpen={(): void => onDocumentTypeSelectOpen && onDocumentTypeSelectOpen()}
                />
              </div>
            </FormInputWrapper>

            {!!document.type &&
              dynamicDocumentInfoFields(index, (documentKeyName: string, documentKeyValue: any) => {
                onDocumentChange(index, {
                  ...document,
                  info: { ...document.info, [documentKeyName]: documentKeyValue },
                });
              })}

            {withFileUpload && document?.type?.code && (
              <FormInputWrapper
                label={document?.type?.code === DocumentTypeCode.PHOTO_CARD ? 'Фото (3.5x4.5)' : documentUploadLabel}
                required
                marginBottomNone
              >
                <FileUpload
                  inputName={attachmentsFieldName}
                  key={document?.type?.id}
                  quantityFiles={quantityFilesByDocumentType[document.type?.code as DocumentTypeCode] ?? 3}
                  inputTitle={
                    document.type?.code === DocumentTypeCode.PHOTO_CARD ? 'Додати фото' : 'Додати сканкопію документа'
                  }
                  value={document.attachments}
                  handleInputChange={async (_, value): Promise<void> => {
                    onDocumentChange(index, {
                      ...documents[index],
                      attachments: value,
                    });
                  }}
                  handleClearInput={(): void => {
                    onDocumentChange(index, {
                      ...documents[index],
                      attachments: [],
                    });
                    handleClearInput && handleClearInput(attachmentsFieldName);
                  }}
                />
              </FormInputWrapper>
            )}

            {index > 0 && (
              <FormInputWrapper marginBottomNone>
                <div className="form-button_remove-label">
                  <Image src="/images/delete-icon.svg" alt="delete" />
                  <input
                    className="form-button_remove"
                    type="button"
                    name="removeDoc"
                    value="Видалити документ"
                    onClick={(): void => {
                      handleChange(documents.filter((_, itemIndex) => itemIndex !== index));
                    }}
                  />
                </div>
              </FormInputWrapper>
            )}
          </React.Fragment>
        );
      })}

      {documents.length < maxDocumentsCount && (
        <FormRow>
          <Button
            size="fit"
            disabled={!documents.length || (documents.length === 1 && isEmpty(documents[0][DocumentFieldName.type]))}
            outline
            onClick={addEmptyDocument}
          >
            Додати ще один документ
          </Button>
        </FormRow>
      )}
    </>
  );
};
